/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    withStyles, Switch,
    Button,
} from '@material-ui/core';
import {
    object, shape, string, func, bool, number, oneOfType,
} from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { format } from 'date-fns';
import { trackEvent as track } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import AutoRenewalModal from './AutoRenewalModal';
import { setTokenLocalStorage } from '../../../../../../state/ducks/Member/ducks/Auth/helper/helper';

const styles = (theme) => ({
    modalCancelButton: {
        width: 'fit-content !important',
        margin: '0 !important',
        textTransform: 'capitalize',
        backgroundColor: 'white !important',
        color: 'black !important',
        border: '1px solid black !important',
        borderRadius: '3px !important',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        maxWidth: '600px',
    },
    block: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    labelBox: {
        margin: '0px',
        width: '100px',
        display: 'flex',
        justifyContent: 'center',
    },
    iconRoot: {
        width: 48,
        height: 24,
        padding: 0,
        margin: theme.spacing(1),
    },
    switchBase: {
        padding: 1,
        '&$checked': {
            transform: 'translateX(24px)',
            color: theme.palette.common.white,
            '& + $track': {
                backgroundColor: '#52d869',
                opacity: 1,
                border: 'none',
            },
        },
        '&$focusVisible $thumb': {
            color: '#52d869',
            border: '6px solid #fff',
        },
    },
    thumb: {
        width: 22,
        height: 22,
    },
    track: {
        borderRadius: 36 / 2,
        border: `1px solid ${theme.palette.grey[400]}`,
        backgroundColor: theme.palette.grey[50],
        opacity: 1,
        transition: theme.transitions.create(['background-color', 'border']),
    },
    boldText: {
        fontWeight: 900,
        textTransform: 'capitalize',
        gap: '10px',
        display: 'flex',
    },
    greenText: {
        fontWeight: 900,
        color: 'green',
        fontSize: '18px',
    },
    redText: {
        fontWeight: 900,
        color: 'red',
    },
    checkboxStyle: {
        marginLeft: '10px',
        marginRight: '5px',
    },
    checked: {},
    focusVisible: {},
    copy: {
        lineHeight: '1.45',
    },
    turnOffNotice: {
        lineHeight: '1.45',
    },
});

const AutoRenewal = ({
    classes, userDetails, autoRenewalStatus, renewalPrice, updateAutoRenewal, trackAutoRenewEvent,
}) => {
    const { email } = userDetails;
    const [autoRenewal, setAutoRenewal] = useState({
        checked: true,
    });
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        setAutoRenewal({ checked: autoRenewalStatus });
    }, [autoRenewalStatus]);

    const toggleAutoRenewal = (event) => {
        const newChecked = event;
        // Toggle Off
        if (!newChecked) {
            setShowModal(true);
            trackAutoRenewEvent({
                eventCategory: 'account',
                eventAction: 'pp auto renew off switch click',
                eventLabel: 'passport',
            });
            return;
        }
        // Toggle On
        setAutoRenewal({
            checked: newChecked,
        });
        updateAutoRenewal({ emailId: email, autoRenewFlag: true });
        trackAutoRenewEvent({
            eventCategory: 'account',
            eventAction: 'pp auto renew switched on',
            eventLabel: 'passport',
            eventValue: 1,
        });
        setTokenLocalStorage(true, 'refreshMetadata');
    };

    // user decides not to turn off auto renewal
    const closeModal = () => {
        setAutoRenewal({
            checked: true,
        });
        setShowModal(false);
        trackAutoRenewEvent({
            eventCategory: 'account',
            eventAction: 'overlay closed',
            eventLabel: 'passport',
        });
    };
    const endDate = new Date(userDetails?.endDate);
    const renewalDate = new Date(userDetails?.renewalDate);
    const formattedEndDate = !Number.isNaN(endDate.getTime()) ? `${endDate.getMonth() + 1}/${endDate.getDate()}/${String(endDate.getFullYear()).slice(-2)}` : '';
    const formattedRenewalDate = !Number.isNaN(renewalDate.getTime()) ? `${renewalDate.getMonth() + 1}/${renewalDate.getDate()}/${String(renewalDate.getFullYear()).slice(-2)}` : '';

    return (
        <div className={classes.container}>
            <div className={classes.boldText}>
                <span>Auto-Renewal</span>
                {autoRenewal?.checked
                    ? (<span className={classes.greenText}>On</span>)
                    : (<span className={classes.redText}>Off</span>)}
            </div>
            <div className={classes.copy}>
                <span>{autoRenewal?.checked ? `Your membership will automatically renew on ${formattedRenewalDate} for one year and you will be charged $${renewalPrice}.`
                    : `Your membership will end on ${formattedEndDate}.`}
                </span>
            </div>
            <Button variant="contained" data-testid="cancel-btn-test" className={classes.modalCancelButton} onClick={() => toggleAutoRenewal(!autoRenewal.checked)}>
                {autoRenewal?.checked ?  'Turn Off Auto-Renewal' : 'Turn On Auto-Renewal'}
            </Button>
            <div>
                <span>{autoRenewal?.checked ? `If you turn off Auto-Renewal your membership will end on  ${formattedEndDate}.`
                    : `If you turn on Auto-Renewal your membership will automatically renew on ${formattedRenewalDate} and you will be charged $${renewalPrice} on the renewal date.`}
                </span>
            </div>
            {showModal && (
                <AutoRenewalModal
                    closeModal={closeModal}
                    open={showModal}
                    userDetails={userDetails}
                    setAutoRenewal={setAutoRenewal}
                    setShowModal={setShowModal}
                    updateAutoRenewal={updateAutoRenewal}
                    trackAutoRenewEvent={trackAutoRenewEvent}
                />
            )}
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    trackAutoRenewEvent: bindActionCreators(track, dispatch),
});

AutoRenewal.propTypes = {
    classes: object,
    userDetails: shape({
        renewalDate: string,
    }),
    updateAutoRenewal: func.isRequired,
    autoRenewalStatus: bool.isRequired,
    renewalPrice: oneOfType([number, string]),
    trackAutoRenewEvent: func.isRequired,
};

AutoRenewal.defaultProps = {
    classes: {},
    userDetails: {
        renewalDate: '',
    },
    renewalPrice: '',
};

export default connect(null, mapDispatchToProps)(withStyles(styles)(AutoRenewal));
